/**
 * Developer panel (F2). Plain DOM, built with createElement/textContent only
 * (no HTML injection from preset names or pasted JSON). It is usable while the
 * game is paused: press Esc, adjust, click the view to resume.
 */
import { DEFAULT_CONFIG, type MovementConfig } from '../core/Config';
import { applyPresetRules, BUILTIN_PRESETS, capturePreset, LocalPresetStore, parsePreset, PresetError, type TuningPreset } from './Presets';
import { deriveReadouts, readTunable, TUNABLES, type Tunable, type TunableGroup } from './Tunables';
import type { DevHost, DevTools, ReplaySpeed } from './DevTools';

function h<K extends keyof HTMLElementTagNameMap>(tag: K, props: Partial<HTMLElementTagNameMap[K]> = {}, ...children: (Node | string)[]): HTMLElementTagNameMap[K] {
  const e = Object.assign(document.createElement(tag), props);
  e.append(...children);
  return e;
}

function button(label: string, onClick: () => void, title = ''): HTMLButtonElement {
  const b = h('button', { type: 'button', textContent: label, title });
  b.addEventListener('click', onClick);
  return b;
}

function decimals(step: number): number {
  const s = String(step);
  return s.includes('.') ? s.length - s.indexOf('.') - 1 : 0;
}

export class DevPanel {
  private readonly root: HTMLElement;
  private readonly sliders = new Map<string, { input: HTMLInputElement; out: HTMLOutputElement; t: Tunable }>();
  private readonly readouts: HTMLDListElement;
  private readonly events: HTMLOListElement;
  private readonly graphCanvas: HTMLCanvasElement;
  private readonly status: HTMLElement;
  private readonly presetSelect: HTMLSelectElement;
  private readonly presetName: HTMLInputElement;
  private readonly jsonBox: HTMLTextAreaElement;
  private readonly replayStatus: HTMLElement;
  private readonly spawnStatus: HTMLElement;
  private readonly store: LocalPresetStore;
  private lastDraw = 0;

  constructor(
    host: HTMLElement,
    private readonly dev: DevTools,
    private readonly app: DevHost & { setVectorsVisible(on: boolean): void },
  ) {
    this.store = new LocalPresetStore(app.rules);
    this.root = h('aside', { id: 'devpanel', hidden: true });
    this.root.setAttribute('aria-label', 'Developer tools');
    this.status = h('p', { className: 'dev-status' });
    this.readouts = h('dl', { className: 'dev-readouts' });
    this.events = h('ol', { className: 'dev-events' });
    this.graphCanvas = h('canvas', { width: 320, height: 96, className: 'dev-graph' });
    this.presetSelect = h('select');
    this.presetName = h('input', { type: 'text', placeholder: 'preset name', maxLength: 48 });
    this.jsonBox = h('textarea', { rows: 3, placeholder: 'Preset or recording JSON (copy / paste here)', spellcheck: false });
    this.replayStatus = h('span', { className: 'dev-replay-status' });
    this.spawnStatus = h('span');
    this.build();
    host.append(this.root);
  }

  /** Removes the panel; its listeners live on its own elements and go with it. */
  dispose(): void {
    this.root.remove();
  }

  get visible(): boolean {
    return !this.root.hidden;
  }

  toggle(): void {
    this.root.hidden = !this.root.hidden;
    if (this.visible) this.syncFromConfig();
  }

  /** Call every frame; cheap when hidden. */
  update(now: number): void {
    if (!this.visible || now - this.lastDraw < 50) return;
    this.lastDraw = now;
    const recent = this.dev.log.recent();
    this.dev.graph.draw(this.graphCanvas, recent);
    this.events.replaceChildren(...recent.map((e) => h('li', { className: `ev-${e.kind}`, textContent: `${(e.tick / 120).toFixed(2)}s ${e.kind} · ${e.speed.toFixed(1)} m/s · ${e.detail}` })));
    const r = this.dev.replay;
    const rec = this.dev.recorder;
    this.replayStatus.textContent = rec.recording
      ? `● recording ${(rec.length / 120).toFixed(1)} s`
      : r
        ? `replay ${r.cursor}/${r.total} ${r.status}${r.matched === null ? '' : r.matched ? ' · MATCH (end-state hash)' : ' · DIVERGED'}${r.configMatches ? '' : ' · config differs'}`
        : this.dev.lastRecording
          ? `recording ready (${(this.dev.lastRecording.inputs.length / 120).toFixed(1)} s)`
          : 'no recording';
    const sp = this.dev.spawn;
    this.spawnStatus.textContent = sp ? `spawn ${sp.position.map((n) => n.toFixed(1)).join(', ')}` : 'spawn: track start';
  }

  /** Refreshes slider positions and readouts from the live configuration. */
  syncFromConfig(): void {
    const mv = this.app.sim.config.movement;
    for (const { input, out, t } of this.sliders.values()) {
      const v = readTunable(t, mv, this.app.rules);
      input.value = String(v);
      out.value = v.toFixed(decimals(t.step));
    }
    this.readouts.replaceChildren(...deriveReadouts(mv, this.app.sim.config.simulation.tickRate).flatMap((r) => [h('dt', { textContent: r.label, title: r.help }), h('dd', { textContent: r.value })]));
    this.refreshPresetList();
  }

  // ------------------------------------------------------------ build

  private build(): void {
    const groups = new Map<TunableGroup, Tunable[]>();
    for (const t of TUNABLES) groups.set(t.group, [...(groups.get(t.group) ?? []), t]);
    const tuning = h('section', {}, h('h3', { textContent: 'Movement parameters' }));
    for (const [group, list] of groups) {
      const det = h('details', {}, h('summary', { textContent: group }));
      for (const t of list) det.append(this.sliderRow(t));
      tuning.append(det);
    }
    tuning.append(button('Reset all to defaults', () => this.resetAll()));

    const view = h('label', {}, h('input', { type: 'checkbox' }), ' Show velocity vectors & contact normals');
    const box = view.querySelector('input')!;
    box.addEventListener('change', () => this.app.setVectorsVisible(box.checked));

    this.root.append(
      h('h2', { textContent: 'Developer tools ' }, h('small', { textContent: 'F2 · Esc to use the mouse' })),
      this.status,
      h('section', {}, h('h3', { textContent: 'What these settings mean' }), this.readouts),
      h('section', {}, h('h3', { textContent: 'Speed (10 s)' }), this.graphCanvas, h('p', { className: 'dev-legend', textContent: 'green ground · violet air · cyan vertical · J jump C release L land ^ launch ! impact x death' })),
      h('section', {}, h('h3', { textContent: 'Recent movement events' }), this.events),
      tuning,
      this.presetSection(),
      h(
        'section',
        {},
        h('h3', { textContent: 'Lab spawn' }),
        button('Place here (P)', () => this.dev.placeSpawnHere()),
        button('Go (G)', () => this.dev.gotoSpawn()),
        button('Clear', () => this.dev.clearSpawn()),
        ' ',
        this.spawnStatus,
        ...this.waypointRow(),
      ),
      this.replaySection(),
      h('section', {}, h('h3', { textContent: 'View' }), view),
    );
  }

  private waypointRow(): HTMLElement[] {
    const points = this.app.sim.level.data.waypoints ?? [];
    if (points.length === 0) return [];
    return [h('div', { className: 'dev-row' }, ...points.map((w) => button(w.name, () => this.dev.gotoPoint(w.spawn), `Teleport to ${w.name}`)))];
  }

  private sliderRow(t: Tunable): HTMLElement {
    const input = h('input', { type: 'range', min: String(t.min), max: String(t.max), step: String(t.step) });
    input.dataset['tunable'] = t.id;
    const out = h('output');
    input.addEventListener('input', () => {
      out.value = Number(input.value).toFixed(decimals(t.step));
      this.applyTunable(t, Number(input.value));
    });
    this.sliders.set(t.id, { input, out, t });
    return h('label', { className: 'dev-slider', title: t.help }, h('span', { textContent: t.label }), input, out);
  }

  private applyTunable(t: Tunable, value: number): void {
    try {
      if (t.target.kind === 'movement') {
        this.app.applyMovement({ ...this.app.sim.config.movement, [t.target.field]: value });
      } else {
        this.app.rules.setParam(t.target.rule, t.target.param, value);
        this.app.rulesChanged();
      }
      this.say('');
      this.readouts.replaceChildren(...deriveReadouts(this.app.sim.config.movement, this.app.sim.config.simulation.tickRate).flatMap((r) => [h('dt', { textContent: r.label, title: r.help }), h('dd', { textContent: r.value })]));
    } catch (err: unknown) {
      this.say(err instanceof Error ? err.message : String(err));
      this.syncFromConfig();
    }
  }

  private resetAll(): void {
    this.app.applyMovement(DEFAULT_CONFIG.movement);
    this.syncFromConfig();
    this.say('Movement parameters reset (experiment parameters unchanged)');
  }

  private presetSection(): HTMLElement {
    return h(
      'section',
      {},
      h('h3', { textContent: 'Configurations' }),
      h('div', { className: 'dev-row' }, this.presetName, button('Save', () => this.savePreset())),
      h('div', { className: 'dev-row' }, this.presetSelect, button('Load', () => this.loadSelected()), button('Delete', () => this.deleteSelected())),
      this.jsonBox,
      h('div', { className: 'dev-row' }, button('Copy current as JSON', () => this.exportPreset()), button('Apply JSON', () => this.importPreset())),
    );
  }

  private replaySection(): HTMLElement {
    const speed = h('select');
    for (const s of [1, 0.5, 0.25] as const) speed.append(h('option', { value: String(s), textContent: `${s}×` }));
    speed.addEventListener('change', () => (this.dev.replaySpeed = Number(speed.value) as ReplaySpeed));
    return h(
      'section',
      {},
      h('h3', { textContent: 'Input recording & replay' }),
      h('p', { className: 'dev-legend', textContent: 'Recording starts at the lab spawn (or track start). Replays verify the end state.' }),
      h(
        'div',
        { className: 'dev-row' },
        button('● Record', () => this.dev.startRecording()),
        button('■ Stop', () => {
          if (this.dev.recorder.recording) this.dev.stopRecording();
          else this.dev.stopReplay();
        }),
        button('▶ Replay', () => this.dev.startReplay()),
        button('❚❚', () => this.dev.toggleReplayPause(), 'Pause / resume replay'),
        button('Step', () => this.dev.stepReplay(), 'Advance one tick (replay paused)'),
        speed,
      ),
      h(
        'div',
        { className: 'dev-row' },
        button('Copy recording JSON', () => {
          if (this.dev.lastRecording) this.writeJson(JSON.stringify(this.dev.lastRecording));
        }),
        button('Load recording JSON', () => {
          try {
            this.dev.importRecording(this.jsonBox.value);
            this.say('Recording loaded: press Replay');
          } catch (err: unknown) {
            this.say(`Recording rejected: ${err instanceof Error ? err.message : String(err)}`);
          }
        }),
      ),
      this.replayStatus,
    );
  }

  // ------------------------------------------------------------ presets

  private refreshPresetList(): void {
    const names = [...BUILTIN_PRESETS, ...this.store.list()].map((p) => p.name);
    this.presetSelect.replaceChildren(...names.map((n) => h('option', { value: n, textContent: n })));
  }

  private current(name: string): TuningPreset {
    return capturePreset(name, this.app.sim.config.movement, this.app.rules);
  }

  private savePreset(): void {
    const p = this.current(this.presetName.value);
    this.store.save(p);
    this.refreshPresetList();
    this.presetSelect.value = p.name;
    this.say(`Saved "${p.name}"`);
  }

  private loadSelected(): void {
    const p = [...BUILTIN_PRESETS, ...this.store.list()].find((x) => x.name === this.presetSelect.value);
    if (p) this.applyPreset(p);
  }

  private deleteSelected(): void {
    if (!this.presetSelect.value || BUILTIN_PRESETS.some((p) => p.name === this.presetSelect.value)) return;
    this.store.remove(this.presetSelect.value);
    this.refreshPresetList();
  }

  private exportPreset(): void {
    this.writeJson(JSON.stringify(this.current(this.presetName.value || 'exported')));
  }

  private importPreset(): void {
    try {
      this.applyPreset(parsePreset(JSON.parse(this.jsonBox.value) as unknown, this.app.rules));
    } catch (err: unknown) {
      this.say(`Preset rejected: ${err instanceof PresetError || err instanceof SyntaxError ? err.message : String(err)}`);
    }
  }

  private applyPreset(p: TuningPreset): void {
    const movement: MovementConfig = { ...DEFAULT_CONFIG.movement, ...p.movement };
    applyPresetRules(p, this.app.rules);
    this.app.applyMovement(movement);
    this.app.rulesChanged();
    this.syncFromConfig();
    this.say(`Loaded "${p.name}"`);
  }

  private writeJson(json: string): void {
    this.jsonBox.value = json;
    this.jsonBox.select();
    navigator.clipboard?.writeText(json).then(
      () => this.say('Copied to clipboard'),
      () => this.say('Select the text box and copy'),
    );
  }

  private say(msg: string): void {
    this.status.textContent = msg;
  }
}
